import type { Patch } from "@freightsimple/shared/types";
import { Dropdown } from "@freightsimple/ui-core/Controls/Dropdown";
import {
  FormItem,
  type ErrorMessageType,
} from "@freightsimple/ui-core/FormLayout/FormItem";
import { Grid } from "@freightsimple/ui-core/PageLayout/Grid";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { TextInput } from "@freightsimple/ui-core/Controls/TextInput";

import type { Address } from "@freightsimple/generated-dashboard-openapi-client/models/Address";
import { CountryCode } from "@freightsimple/generated-dashboard-openapi-client/models/CountryCode";

interface BillingAddressInputProps {
  address?: Patch<Address> | undefined;
  addressChanged: (_: Patch<Address>) => void;
  enabled: boolean;
  forceValidation: boolean;
  errorMessageForAddress: ErrorMessageType;
  errorMessageForCity: ErrorMessageType;
  errorMessageForStateOrProvinceCode: ErrorMessageType;
  locationsErrorMessageForPostalCode: ErrorMessageType;
  errorMessageForCountry: ErrorMessageType;
}

const COUNTRY_OPTIONS = [
  { value: CountryCode.Canada, label: "Canada" },
  { value: CountryCode.UnitedStates, label: "United States" },
];

const CreditCardsBillingAddressInput: React.FC<BillingAddressInputProps> = (
  props: BillingAddressInputProps,
) => {
  const disabled = !props.enabled;

  function postalCodeDescription() {
    if (props.address?.countryCode === undefined)
      return "Postal Code / Zipcode";
    if (props.address.countryCode === CountryCode.Canada) return "Postal Code";
    if (props.address.countryCode === CountryCode.UnitedStates)
      return "Zipcode";
    throw new Error("Invalid country code");
  }

  function provinceDescription() {
    if (props.address?.countryCode === undefined) return "Province / State";
    if (props.address.countryCode === CountryCode.Canada) return "Province";
    if (props.address.countryCode === CountryCode.UnitedStates) return "State";
    throw new Error("Invalid country code");
  }

  return (
    <Stack gap={4}>
      <FormItem label="Address" error={props.errorMessageForAddress}>
        <TextInput
          data-testid="billing-address"
          value={props.address?.addressLine}
          disabled={disabled}
          onChange={(e) =>
            props.addressChanged({ addressLine: e.target.value })
          }
        />
      </FormItem>
      <FormItem label="Other details (eg. Unit / Floor / Door)">
        <TextInput
          data-testid="billing-address2"
          value={props.address?.addressLine2}
          disabled={disabled}
          onChange={(e) =>
            props.addressChanged({ addressLine2: e.target.value })
          }
        />
      </FormItem>
      <Grid columns={2} gap="sm">
        <FormItem label="City" error={props.errorMessageForCity}>
          <TextInput
            data-testid="billing-city"
            value={props.address?.city}
            disabled={disabled}
            onChange={(e) => props.addressChanged({ city: e.target.value })}
          />
        </FormItem>
        <FormItem
          label={provinceDescription()}
          error={props.errorMessageForStateOrProvinceCode}
        >
          <TextInput
            data-testid="billing-province"
            value={props.address?.stateOrProvinceCode}
            disabled={disabled}
            onChange={(e) =>
              props.addressChanged({ stateOrProvinceCode: e.target.value })
            }
          />
        </FormItem>
      </Grid>
      {/* minColumnWidth, so these pairs stack instead of squashing: inside the
          add-card dialog this column is only ~300px, which left the Country
          dropdown too narrow for "Select country" to fit on one line. On the
          full-width form they still sit side by side. */}
      <Grid columns={2} gap="sm" minColumnWidth={170}>
        <FormItem
          label={postalCodeDescription()}
          error={props.locationsErrorMessageForPostalCode}
        >
          <TextInput
            data-testid="billing-postal-code"
            value={props.address?.postalCode}
            disabled={disabled}
            onChange={(e) =>
              props.addressChanged({ postalCode: e.target.value })
            }
          />
        </FormItem>
        <FormItem label="Country" error={props.errorMessageForCountry}>
          <Dropdown
            data-testid="billing-country"
            placeholder="Select country"
            value={props.address?.countryCode}
            onChange={(v) =>
              props.addressChanged({ countryCode: v as CountryCode })
            }
            options={COUNTRY_OPTIONS}
          />
        </FormItem>
      </Grid>
    </Stack>
  );
};
export default CreditCardsBillingAddressInput;
